// LanguageSelectField — THE one language control for the whole platform.
//
// Surfaces that need a "pick a language" field and must not drift:
//   • the legacy Platform Options grid (pages/settings.js);
//   • the workspace ADMIN default (workspace-shell
//     pages/admin/workspace-settings.js — moved there from
//     pages/admin/dashboard-defaults.js on 2026-09-02), which reaches this by
//     bare key through the shared-library component merge.
//
// The ACCOUNT PAGE carries NO language control since 2026-09-10 (Nika:
// "default language is english. only workspaces that are specifically set as
// Georgian should render in georgian"): the interface language is the
// WORKSPACE's own setting (functions/languages.js resolveLang), so a per-person
// switcher there changed nothing past the next reload. Its Language card had
// carried `InterfaceLanguageSelect` until 2026-09-02, then `LangSwitcher`
// (components/LangSwitcher.js) — the designed dropdown, which now ships in
// the barrel and is mounted on no shell surface. `InterfaceLanguageSelect` + `LanguageOption` are consequently
// UNMOUNTED: kept as the no-portal form of the same control (a native select
// needs no DropdownRoot), not as a live surface. Delete them, or mount them,
// before treating them as either.
//
// Two exports, split by what varies:
//
//   LanguageSelectField     the CHROME and the OPTION LIST. It knows which
//                           languages ship and how to label them. It knows
//                           nothing about where the current value lives or
//                           who writes it, so the admin surface can bind it
//                           to `workspace.settings.language` and an SDK
//                           write without fighting a built-in handler.
//
//   InterfaceLanguageField  the PERSON's own language, bound. Reads
//                           `root.lang`, writes through `setLang`. This is
//                           what every user-facing surface mounts, so the
//                           read and the write exist in ONE place instead
//                           of once per page.
//
// Callers supply only their own labels (`Title.text`, the dropdown header)
// and, for LanguageSelectField, the value + handler.
//
// THE CALLER STILL OWNS ITS OWN LABELS. This file wires only the strings it
// declares itself (the select's `aria-label`); a `Title.text` belongs to the
// surface that mounts the control, because each surface names the control
// differently.
//
// Polyglot IS available everywhere now. `context.polyglot` moved into this
// package's own context.js, and `mergeOneLibrary` copies a top-level key
// wholesale into any consumer that does not declare one — so canvas,
// marketplace and preview all inherit it. Verified by running the real
// `mergeSharedLibraries` over canvas's real context: polyglot absent before,
// present after, 77 keys per language.
//
// It was NOT available when this file was written, and the failure mode is
// worth keeping written down: with the filter unregistered,
// `replaceLiteralsWithObjectFields` returns '' (smbls/packages/utils/string.js)
// and `el.call` returns `undefined` (element/src/methods.js), which the text
// effect skips on its `val != null` guard. Both polyglot forms render EMPTY,
// never the key. Any surface that mounts this control without the shared
// library must supply plain strings.
//
// Every language is labelled by its ENDONYM — its own name in its own
// language. "Georgian" is unreadable to the exact person hunting for
// Georgian.
//
// The canonical registry is functions/languages.js. Nothing here restates a
// language list; adding a language stays ONE edit to PLATFORM_LANGUAGES.
//
// FA204 / frank trap — the registry is read at DEFINITION time for the
// static `options` array, and carried on `scope` for the value label, so no
// serialized handler ever reads a bare module-scope binding. Same shape as
// components/AiModelSwitcher.js.
import { PLATFORM_LANGUAGES } from '../functions/languages.js'

export const LanguageSelectField = {
  extends: 'SelectDropdownWithTitle',

  scope: {
    LANGS: PLATFORM_LANGUAGES,
    SUPPORTED: ['en', 'ka'],
    // Code → the language's own name. Falls back to the registry's first
    // entry so a code the platform no longer ships cannot leave the control
    // blank.
    endonym(code) {
      const list = this.LANGS || []
      for (const lang of list) if (lang.code === code) return lang.endonym
      return list.length ? list[0].endonym : ''
    },
    // The ONE language `setLang` accepts inside the active workspace, or null
    // when it accepts any (no active workspace — a standalone or public
    // surface). THE LAW (Nika, 2026-09-10): inside a workspace its RESOLVED
    // language rules — enforced pair → `settings.language` → the white-label
    // brand → English — under 'default' and 'enforced' alike, and
    // `applyWorkspaceLangPolicy` re-applies it on every active-workspace event.
    // A record still IN FLIGHT (an id, no `settings`, not stamped full) holds
    // the active language, exactly as the policy and `setLang` hold it.
    //
    // Declared HERE, on the unbound chrome, rather than on
    // `InterfaceLanguageField` below, for a mechanical reason: `scope` merge
    // semantics across an `extends` chain are not something this control
    // should depend on, and a second `scope` block on the subclass would put
    // `endonym` (read by a nested prop) at the mercy of them. Only
    // `InterfaceLanguageField` CALLS it. The workspace-admin card also extends
    // this component and must never lock: the value it edits is the workspace
    // default itself, not the reader's own language.
    //
    // FA204 / frank trap — the resolution is INLINED rather than reading
    // `functions/languages.js`'s `resolveLang` from inside a serialized prop,
    // exactly like the registry inlines its own lists. Pinned to `setLang`'s
    // own gate by __tests__/i18nBootLanguage.test.js.
    lockedLang(root) {
      const ws =
        root && root.workspace && typeof root.workspace === 'object' ? root.workspace : null
      if (!ws) return null
      const list = this.SUPPORTED || []
      const ok = (c) => typeof c === 'string' && list.indexOf(c) !== -1
      const settings = ws.settings && typeof ws.settings === 'object' ? ws.settings : null
      const pending =
        !settings && ws._shape !== 'full' && !!(ws.id || ws._id) && !ws._completionUnreachable
      if (pending) return ok(root.lang) ? root.lang : 'en'
      const brand = root.whitelabelBrand && root.whitelabelBrand.lang
      return (
        (settings && settings.languagePolicy === 'enforced' && ok(settings.language)
          ? settings.language
          : null) ||
        (settings && ok(settings.language) ? settings.language : null) ||
        (ok(brand) ? brand : null) ||
        'en'
      )
    }
  },

  Title: {
    // The adaptive light/dark neutral pair used for every de-emphasized
    // field label in this family — at full strength. It also carried
    // opacity .65, which faded the label to 4.66:1 in light (admin audit,
    // 2026-09-24): the quiet ink is the de-emphasis, never an opacity.
    color: 'caption'
  },

  SelectDropdown: {
    Dropdown: {
      top: '121%',
      options: PLATFORM_LANGUAGES.map((lang) => ({
        key: lang.code,
        text: lang.endonym
      })),
      ListInDropdown: {
        childProps: {
          extends: 'KeyValueColumnFields',
          // NO `text:` here. KeyValueColumnFields already renders the option
          // label in its own `Text` slot (`s.text || s.value || s.key`), and
          // this row HAS children, so a `text:` on the row adds a SECOND text
          // node beside them: every option read "English ✓ English". The row
          // is a chassis with slots, not a leaf.
          fontWeight: '400'
        }
      }
    }
  }
}

// The person's OWN interface language, fully bound.
//
// `setLang` owns the ENTIRE write — the loaded i18n bundles, polyglot,
// <html lang>/<dir>, localStorage and the account preference through
// sdk.updateMe. A surface that adds a second writer drifts from it.
export const InterfaceLanguageField = {
  extends: 'LanguageSelectField',

  SelectDropdown: {
    // LOCKED whenever there is an active workspace: its resolved language
    // rules (see `lockedLang` above). `DropdownField` is a real
    // `<button>`, so `disabled` is the whole mechanism: the panel cannot open,
    // the control leaves the tab order, and assistive tech announces the state
    // — no bespoke look, no second affordance, and the `:disabled` styling
    // stays owned by the primitive (Rule 65).
    disabled: (el, s) => !!el.scope.lockedLang(s.root),
    Value: {
      Text: {
        // Reads the ACTIVE language off ROOT STATE, never a snapshot taken
        // at render: the shell's LangSwitcher, another tab, or a fresh
        // account preference can all change it, and this control has to
        // follow rather than describe a stale choice.
        //
        // Under a lock it reads the ENFORCED language instead. The two agree
        // once the policy has been applied, but this prop also paints during
        // the window before it, and a locked field must never show a value
        // the workspace refuses.
        //
        // FA515 — `s.root.lang` is read UNCONDITIONALLY, into a local, BEFORE
        // the lock is consulted. Reading it on the right of a `||` would mean
        // a locked run short-circuits past the signal and never tracks it, so
        // unlocking (leaving the enforced workspace) would leave this label
        // frozen on the enforced language. Same trap the original guard
        // (`s.root && s.root.lang`) was written to avoid.
        text: (el, s) => {
          const active = s.root.lang
          return el.scope.endonym(el.scope.lockedLang(s.root) || active)
        }
      }
    },
    Dropdown: {
      onChoose: (ev, el, s, ctx, calleeElement) => {
        // Belt to `disabled`'s braces. A disabled button cannot open this
        // panel, so this is unreachable through the UI — it is here because a
        // control that writes must state its own refusal, not inherit it from
        // a sibling prop that a later edit could drop. `setLang` refuses a
        // third time, which is what covers callers that are not this control.
        if (el.scope.lockedLang(s.root)) return
        el.call('setLang', calleeElement.state.key)
      },
      ListInDropdown: {
        childProps: {
          // FA515 — `lang` read unconditionally first; see `Value.Text` above.
          isActive: (el, s) => {
            const active = s.root.lang
            return (el.scope.lockedLang(s.root) || active) === s.key
          }
        }
      }
    }
  },

  // The one line that says WHY the field is dead. `if:` (not `hide:`) so it
  // does not exist where nothing locks (no active workspace), and LAST so the
  // gate resolving after the workspace record hydrates moves nothing already
  // on screen (Rule 66). `Group` (this control's chassis, via
  // SelectDropdownWithTitle → GroupField) is `flow: 'y'`, so it lands directly
  // under the control it qualifies — where the reader needs it, after they
  // have seen the control.
  Caption: {
    tag: 'span',
    if: (el, s) => !!el.scope.lockedLang(s.root),
    fontSize: 'Z2',
    color: 'caption',
    // The FUNCTION form, not `{{ … | polyglot }}`. Nothing on the account
    // path carries a polyglot TEMPLATE (pinned by LanguageSelectField.test.js)
    // — canvas serves this same page at its own /settings, and the function
    // form is the one that registers its own effect and cannot be frozen by
    // the static attribute pass.
    text: (el) => el.call('polyglot', 'common.language.setByWorkspace')
  }
}

// The same choice, in a native-select chrome. UNMOUNTED since 2026-09-02 —
// the account page it was written for now mounts `LangSwitcher` instead (see
// this file's header). It stays because it is the only form of this control
// that needs no DropdownRoot; the notes below describe the surface it was
// built for, not a surface it is on today.
//
// components/MemberProfile.js states its choices as native `<select>` rows
// (`DigestSelect`, `MentionSelect`, …) inside a RowLabel + control row. A
// brand dropdown dropped into that column would read as a foreign control on
// the one page a person actually reaches. So the CHROME follows the page and
// the DATA follows the registry: options, endonyms and the `setLang` write
// are the same single source of truth the two dropdown forms above use.
//
// `value:` (controlled), not `<option selected>`: a native <select> ignores
// declarative selectedness when its options arrive through `childrenAs`, and
// smbls re-asserts a controlled `value` on every option remount
// (element/src/create.js syncSelectValue). The neighbouring rows predate that
// note; this one uses the form that survives a re-render.
//
// The label is a plain string, NOT a polyglot template. This component mounts
// under canvas, which registers no `context.polyglot`, and BOTH polyglot forms
// render empty there — see this file's header for the three framework sites
// that produce that.
export const InterfaceLanguageSelect = {
  tag: 'select',
  theme: 'field',
  minHeight: 'controlH',
  padding: '0 A',
  round: 'radiusControl',
  color: 'title',
  fontSize: 'Z2',
  cursor: 'pointer',
  // The visible RowLabel is not associated with this control by `for`/`id`,
  // so a screen reader would otherwise announce an unnamed combo box.
  //
  // A FUNCTION, not a `{{ … | polyglot }}` string. A static template on an
  // ATTRIBUTE resolves exactly ONCE, in applyAttrsInProps, which then
  // OVERWRITES the prop with the resolved text (element/src/create.js) — the
  // label would freeze in whatever language was active at first paint. Only
  // `text:` gets a reactive template effect. A function-valued flat attribute
  // registers its own createEffect, and `polyglot` reads `root.lang` through
  // `getActiveLang`, so the effect re-fires on every language switch.
  //
  // `settings.language.title` is an EXISTING key in i18n/{en,ka}/settings.json:
  // "Interface language" / "ინტერფეისის ენა". Both bundles ship in
  // `context.polyglot.translations`, so no fetch is involved.
  'aria-label': (el) => el.call('polyglot', 'settings.language.title'),

  scope: { LANGS: PLATFORM_LANGUAGES },

  // FA808 / Rule 10 — a NAMED component, never an inline `childExtends: {…}`
  // object: frank's serializer cannot carry the inline form, so the published
  // build would render an option-less select. (The neighbouring DigestSelect /
  // MentionSelect rows in MemberProfile.js still use the inline shape; that is
  // pre-existing debt, not a licence to add another one.)
  childExtends: 'LanguageOption',
  childrenAs: 'state',
  children: (el) => el.scope.LANGS.map((lang) => ({ value: lang.code, text: lang.endonym })),

  // FA515 — `s.root.lang` is read UNCONDITIONALLY so the signal is tracked and
  // the control follows a language changed from the shell switcher or another
  // tab.
  value: (el, s) => s.root.lang,

  // `setLang` owns the entire write. No second writer here.
  onChange: (e, el) => el.call('setLang', e.target.value)
}

// One <option> of a native language select. Named so `childExtends` can be a
// quoted string (Rule 10) — the only form that survives frank.toJSON.
export const LanguageOption = {
  tag: 'option',
  value: (el, s) => s.value,
  text: (el, s) => s.text || s.value || ''
}
